/**
 * Black Matte VTC - Create New Partner Booking
 * Pure view layer - ALL validation/blocking from API
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Users,
  Phone,
  Mail,
  User,
  Building2,
  FileText,
  AlertTriangle,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';

const PartnerBookingNew = () => {
  const navigate = useNavigate();
  
  // Balance/credit state - from API
  const [balanceData, setBalanceData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    pickup_address: '',
    destination_address: '',
    date: '',
    time: '',
    passengers: '1',
    guest_name: '',
    room_number: '',
    phone: '',
    email: '',
    reference: '',
    notes: ''
  });
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch credit status from API on mount
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const data = await partnerService.getBalance();
        setBalanceData(data);
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      } finally {
        setBalanceLoading(false);
      }
    };
    fetchBalance();
  }, []);

  // Check if booking is blocked - FROM API ONLY
  const isBlocked = balanceData?.has_overdue_invoices || !balanceData?.booking_allowed;
  const blockReason = balanceData?.has_overdue_invoices 
    ? 'overdue_invoices' 
    : !balanceData?.booking_allowed 
      ? 'credit_limit_exceeded' 
      : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);

    try {
      // Build request data - filter out undefined/empty values
      const bookingData = {
        pickup_address: formData.pickup_address,
        destination_address: formData.destination_address,
        date: formData.date,
        time: formData.time,
        passengers: parseInt(formData.passengers, 10)
      };
      
      // Add optional fields only if they have values
      if (formData.guest_name) bookingData.guest_name = formData.guest_name;
      if (formData.room_number) bookingData.room_number = formData.room_number;
      if (formData.phone) bookingData.phone = formData.phone;
      if (formData.email) bookingData.email = formData.email;
      if (formData.reference) bookingData.reference = formData.reference;
      if (formData.notes) bookingData.notes = formData.notes;

      // Send request to API - backend decides if allowed
      const result = await partnerService.createBooking(bookingData);

      setSuccess({
        booking_reference: result.booking_reference,
        estimated_fare: result.estimated_fare
      });

      // Redirect after short delay
      setTimeout(() => {
        navigate(`/partner/bookings/${result.booking_id}`);
      }, 2000);

    } catch (err) {
      // Handle API errors - display as-is
      const errorMessage = err.message || 'Failed to create booking';
      
      // Detect specific error types from API
      if (errorMessage.includes('credit') || errorMessage.includes('Credit')) {
        setError({ type: 'credit_limit_exceeded', message: errorMessage });
      } else if (errorMessage.includes('overdue') || errorMessage.includes('Overdue')) {
        setError({ type: 'overdue_block', message: errorMessage });
      } else if (errorMessage.includes('invalid') || errorMessage.includes('Invalid')) {
        setError({ type: 'invalid_booking', message: errorMessage });
      } else if (errorMessage.includes('expired') || errorMessage.includes('Expired') || errorMessage.includes('401')) {
        setError({ type: 'auth_expired', message: 'Session expired. Please login again.' });
      } else {
        setError({ type: 'unknown', message: errorMessage });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Blocked state UI
  if (balanceLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48 bg-neutral-800" />
        <Skeleton className="h-[600px] w-full bg-neutral-800" />
      </div>
    );
  }

  if (isBlocked) {
    return (
      <div className="space-y-6" data-testid="booking-blocked">
        <Button variant="ghost" asChild className="text-neutral-400 hover:text-white">
          <Link to="/partner/bookings">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Bookings
          </Link>
        </Button>

        <Card className="bg-neutral-900 border-neutral-800 max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">
              Booking Unavailable
            </h2>
            
            <p className="text-neutral-400 mb-6">
              {blockReason === 'overdue_invoices' 
                ? 'You have overdue invoices that must be settled before creating new bookings.'
                : 'Your credit limit has been reached. Please make a payment to continue.'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {blockReason === 'overdue_invoices' ? (
                <Button asChild className="bg-white text-black hover:bg-neutral-200">
                  <Link to="/partner/invoices">View Invoices</Link>
                </Button>
              ) : (
                <Button asChild className="bg-white text-black hover:bg-neutral-200">
                  <Link to="/partner/payments">Make Payment</Link>
                </Button>
              )}
              <Button variant="outline" asChild className="border-neutral-700">
                <Link to="/partner/dashboard">Back to Dashboard</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-booking-new">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" asChild className="text-neutral-400 hover:text-white">
          <Link to="/partner/bookings">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">New Booking</h1>
          <p className="text-neutral-500">Create a transportation booking for your guest</p>
        </div>
      </div>

      {/* Credit Status Banner */}
      {balanceData && (
        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Credit Available</p>
                  <p className="text-xs text-neutral-500">
                    €{balanceData.credit_available?.toFixed(0)} remaining of €{balanceData.credit_limit?.toFixed(0)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Message */}
      {success && (
        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-sm font-medium text-emerald-400">
                  Booking Created Successfully!
                </p>
                <p className="text-xs text-emerald-400/80">
                  Reference: {success.booking_reference} • Estimated: €{success.estimated_fare?.toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message - Display API errors */}
      {error && (
        <Card className={`border ${
          error.type === 'credit_limit_exceeded' || error.type === 'overdue_block'
            ? 'bg-red-500/10 border-red-500/20'
            : 'bg-amber-500/10 border-amber-500/20'
        }`} data-testid="booking-error">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${
                error.type === 'credit_limit_exceeded' || error.type === 'overdue_block'
                  ? 'text-red-500'
                  : 'text-amber-500'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  error.type === 'credit_limit_exceeded' || error.type === 'overdue_block'
                    ? 'text-red-400'
                    : 'text-amber-400'
                }`}>
                  {error.type === 'credit_limit_exceeded' && 'Credit Limit Exceeded'}
                  {error.type === 'overdue_block' && 'Account Blocked'}
                  {error.type === 'invalid_booking' && 'Invalid Booking Details'}
                  {error.type === 'auth_expired' && 'Session Expired'}
                  {error.type === 'unknown' && 'Booking Failed'}
                </p>
                <p className={`text-xs mt-1 ${
                  error.type === 'credit_limit_exceeded' || error.type === 'overdue_block'
                    ? 'text-red-400/80'
                    : 'text-amber-400/80'
                }`}>
                  {error.message}
                </p>
              </div>
              {(error.type === 'credit_limit_exceeded' || error.type === 'overdue_block') && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  asChild
                  className="border-red-500/30 text-red-400"
                >
                  <Link to={error.type === 'overdue_block' ? '/partner/invoices' : '/partner/payments'}>
                    {error.type === 'overdue_block' ? 'View Invoices' : 'Make Payment'}
                  </Link>
                </Button>
              )}
              {error.type === 'auth_expired' && (
                <Button 
                  size="sm" 
                  variant="outline"
                  asChild
                  className="border-amber-500/30 text-amber-400"
                >
                  <Link to="/partner/login">Login Again</Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Booking Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trip Details */}
          <Card className="bg-neutral-900 border-neutral-800 lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Trip Details
              </CardTitle>
              <CardDescription>Enter pickup and destination addresses</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-300">Pickup Address *</Label>
                <Input
                  name="pickup_address"
                  value={formData.pickup_address}
                  onChange={handleChange}
                  placeholder="Enter pickup location"
                  required
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="pickup-address"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Destination Address *</Label>
                <Input
                  name="destination_address"
                  value={formData.destination_address}
                  onChange={handleChange}
                  placeholder="Enter destination"
                  required
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="destination-address"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-neutral-300 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Date *
                  </Label>
                  <Input
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="booking-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-300 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Time *
                  </Label>
                  <Input
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="booking-time"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-300 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Passengers *
                  </Label>
                  <Select 
                    value={formData.passengers} 
                    onValueChange={(v) => handleSelectChange('passengers', v)}
                  >
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white" data-testid="passengers">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      {[1, 2, 3, 4, 5, 6, 7].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} passenger{n > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Guest & Contact */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5" />
                Guest Info
              </CardTitle>
              <CardDescription>Optional guest details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-300">Guest Name</Label>
                <Input
                  name="guest_name"
                  value={formData.guest_name}
                  onChange={handleChange}
                  placeholder="Guest full name"
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="guest-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Room Number
                </Label>
                <Input
                  name="room_number"
                  value={formData.room_number}
                  onChange={handleChange}
                  placeholder="e.g., 405"
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+33 6 12 34 56 78"
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="phone"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="guest@email.com"
                  className="bg-neutral-800 border-neutral-700 text-white"
                  data-testid="email"
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card className="bg-neutral-900 border-neutral-800 lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-neutral-300">Your Reference (Optional)</Label>
                  <Input
                    name="reference"
                    value={formData.reference}
                    onChange={handleChange}
                    placeholder="Internal reference number"
                    className="bg-neutral-800 border-neutral-700 text-white"
                    data-testid="reference"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-neutral-300">Notes</Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Special instructions, flight number, etc."
                  rows={3}
                  className="bg-neutral-800 border-neutral-700 text-white resize-none"
                  data-testid="notes"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 mt-6">
          <Button 
            type="button" 
            variant="outline" 
            asChild
            className="border-neutral-700"
          >
            <Link to="/partner/bookings">Cancel</Link>
          </Button>
          <Button
            type="submit"
            className="bg-white text-black hover:bg-neutral-200"
            disabled={submitting}
            data-testid="submit-booking"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Booking'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PartnerBookingNew;
